import { useState, useCallback } from 'react'
import { PageHeader, colors, input, btnPrimary, btnSecondary } from '../../components/ui'
import { getAuthToken } from '../../services/api'
import { useApiList } from '../../hooks/use-api-list'
import type { Organization } from '@alpha-edge/shared'

type Entity = 'results' | 'patients' | 'orders'
type Format = 'json' | 'ndjson'

interface ExportState {
  readonly loading: boolean
  readonly error: string | null
  readonly lastRowCount: number | null
}

const ENTITIES: ReadonlyArray<{ value: Entity; label: string }> = [
  { value: 'results', label: 'Results' },
  { value: 'patients', label: 'Patients' },
  { value: 'orders', label: 'Orders' },
]

const FORMATS: ReadonlyArray<{ value: Format; label: string; mime: string; ext: string }> = [
  { value: 'json', label: 'JSON (buffered, max 100k rows)', mime: 'application/json', ext: 'json' },
  { value: 'ndjson', label: 'NDJSON (streaming, no cap)', mime: 'application/x-ndjson', ext: 'ndjson' },
]

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: colors.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  marginBottom: '6px',
}

const chipStyle = (selected: boolean): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '4px 12px',
  borderRadius: '9999px',
  fontSize: '0.8125rem',
  fontWeight: 600,
  cursor: 'pointer',
  border: selected ? `2px solid ${colors.primary}` : `2px solid ${colors.border}`,
  background: selected ? '#eff6ff' : '#fff',
  color: selected ? colors.primary : colors.text,
  userSelect: 'none',
  transition: 'all 0.12s',
})

const chipRemoveStyle: React.CSSProperties = {
  marginLeft: '6px',
  fontSize: '0.75rem',
  cursor: 'pointer',
  color: colors.textMuted,
  lineHeight: 1,
}

const formRowStyle: React.CSSProperties = {
  marginBottom: '20px',
}

const tooltipStyle: React.CSSProperties = {
  display: 'inline-block',
  marginLeft: '6px',
  fontSize: '0.75rem',
  color: colors.textMuted,
  background: '#f1f5f9',
  border: `1px solid ${colors.border}`,
  borderRadius: '4px',
  padding: '2px 6px',
  cursor: 'help',
  verticalAlign: 'middle',
}

export function MetaAnalyticsPage() {
  const [entity, setEntity] = useState<Entity>('results')
  const [format, setFormat] = useState<Format>('json')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [selectedOrgIds, setSelectedOrgIds] = useState<readonly string[]>([])
  const [anonymize, setAnonymize] = useState(false)
  const [exportState, setExportState] = useState<ExportState>({
    loading: false,
    error: null,
    lastRowCount: null,
  })

  const { data: orgs, loading: orgsLoading } = useApiList<Organization>({ path: '/organizations' })

  const toggleOrg = useCallback((id: string) => {
    setSelectedOrgIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }, [])

  const handleExport = useCallback(async () => {
    setExportState({ loading: true, error: null, lastRowCount: null })

    const token = getAuthToken()
    const body = {
      entity,
      format,
      orgIds: selectedOrgIds.length > 0 ? [...selectedOrgIds] : undefined,
      from: from || undefined,
      to: to || undefined,
      anonymize,
    }

    try {
      const res = await fetch('/api/admin/meta/export', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      })

      if (!res.ok) {
        const errBody = (await res.json().catch(() => ({}))) as { error?: string; limit?: number }
        if (res.status === 413) {
          throw new Error(
            `Export exceeds ${errBody.limit ?? 100000} row JSON limit. Use NDJSON format for large datasets.`,
          )
        }
        throw new Error(errBody.error ?? `Export failed (${res.status})`)
      }

      const blob = await res.blob()
      const fmt = FORMATS.find((f) => f.value === format)!
      const filename = `${entity}-export.${fmt.ext}`
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      // For JSON responses, extract rowCount from the parsed response
      let rowCount: number | null = null
      if (format === 'json') {
        try {
          const text = await blob.text()
          const parsed = JSON.parse(text) as { data?: unknown[] }
          rowCount = parsed.data?.length ?? null
        } catch {
          // best-effort
        }
      }

      setExportState({ loading: false, error: null, lastRowCount: rowCount })
    } catch (err) {
      setExportState({
        loading: false,
        error: err instanceof Error ? err.message : 'Export failed',
        lastRowCount: null,
      })
    }
  }, [entity, format, selectedOrgIds, from, to, anonymize])

  const selectedOrgs = orgs.filter((o) => selectedOrgIds.includes(o.id))
  const unselectedOrgs = orgs.filter((o) => !selectedOrgIds.includes(o.id))

  return (
    <div>
      <PageHeader title="Meta Analytics" subtitle="Cross-organization data export for research and benchmarking" />

      <div style={{ padding: '24px', maxWidth: '860px' }}>
        {/* Entity */}
        <div style={formRowStyle}>
          <label style={labelStyle}>Entity</label>
          <select
            value={entity}
            onChange={(e) => setEntity(e.target.value as Entity)}
            style={{ ...input, width: '220px' }}
          >
            {ENTITIES.map((e) => (
              <option key={e.value} value={e.value}>
                {e.label}
              </option>
            ))}
          </select>
        </div>

        {/* Format */}
        <div style={formRowStyle}>
          <label style={labelStyle}>Format</label>
          <select
            value={format}
            onChange={(e) => setFormat(e.target.value as Format)}
            style={{ ...input, width: '320px' }}
          >
            {FORMATS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </div>

        {/* Date range */}
        <div style={{ ...formRowStyle, display: 'flex', gap: '16px' }}>
          <div>
            <label style={labelStyle}>From</label>
            <input
              type="datetime-local"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              style={{ ...input, width: '200px' }}
            />
          </div>
          <div>
            <label style={labelStyle}>To</label>
            <input
              type="datetime-local"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              style={{ ...input, width: '200px' }}
            />
          </div>
        </div>

        {/* Org filter chips */}
        <div style={formRowStyle}>
          <label style={labelStyle}>Filter by Organization (all orgs if empty)</label>
          {selectedOrgs.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
              {selectedOrgs.map((org) => (
                <span key={org.id} style={chipStyle(true)}>
                  {org.name}
                  <span
                    style={chipRemoveStyle}
                    role="button"
                    onClick={() => toggleOrg(org.id)}
                    title={`Remove ${org.name}`}
                  >
                    x
                  </span>
                </span>
              ))}
            </div>
          )}
          {orgsLoading ? (
            <span style={{ fontSize: '0.8125rem', color: colors.textMuted }}>Loading organizations...</span>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {unselectedOrgs.map((org) => (
                <span
                  key={org.id}
                  style={chipStyle(false)}
                  role="button"
                  onClick={() => toggleOrg(org.id)}
                  title={`Add ${org.name}`}
                >
                  {org.name}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Anonymize */}
        <div style={formRowStyle}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={anonymize}
              onChange={(e) => setAnonymize(e.target.checked)}
              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
            />
            <span style={{ fontSize: '0.9375rem', fontWeight: 600, color: colors.text }}>Anonymize output</span>
            <span
              style={tooltipStyle}
              title="Strips names, MRN, DOB, free-text notes; rewrites patient IDs via HMAC. Requires ANALYTICS_HMAC_SECRET on the server."
            >
              ?
            </span>
          </label>
          {anonymize && (
            <p style={{ marginTop: '6px', fontSize: '0.8125rem', color: colors.textMuted }}>
              Strips names, MRN, DOB, free-text notes; rewrites patient IDs via HMAC.
            </p>
          )}
        </div>

        {/* Error */}
        {exportState.error && (
          <div
            style={{
              marginBottom: '16px',
              padding: '12px 16px',
              background: '#fee2e2',
              border: '1px solid #fca5a5',
              borderRadius: '6px',
              color: '#dc2626',
              fontSize: '0.875rem',
            }}
          >
            {exportState.error}
          </div>
        )}

        {/* Success indicator */}
        {exportState.lastRowCount !== null && !exportState.error && (
          <div
            style={{
              marginBottom: '16px',
              padding: '12px 16px',
              background: '#f0fdf4',
              border: '1px solid #86efac',
              borderRadius: '6px',
              color: '#166534',
              fontSize: '0.875rem',
            }}
          >
            Export downloaded ({exportState.lastRowCount.toLocaleString()} rows).
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <button style={btnPrimary} onClick={handleExport} disabled={exportState.loading}>
            {exportState.loading ? 'Exporting...' : 'Export'}
          </button>
          {(selectedOrgIds.length > 0 || from || to) && (
            <button
              style={btnSecondary}
              onClick={() => {
                setSelectedOrgIds([])
                setFrom('')
                setTo('')
              }}
            >
              Clear filters
            </button>
          )}
        </div>

        {exportState.loading && (
          <p style={{ marginTop: '12px', fontSize: '0.8125rem', color: colors.textMuted }}>Preparing export...</p>
        )}
      </div>
    </div>
  )
}
